import { Button, Divider, DrawerBody, DrawerHeaderTitle, Image, makeStyles, OverlayDrawer, shorthands, Text, tokens, Tooltip } from '@fluentui/react-components';
import { Dismiss24Regular, QuestionCircleFilled } from '@fluentui/react-icons';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionTypes, AttachmentSourceType, GptTypes, IConversation, ISource } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useIsMobile } from '../../Hooks/useResponsive';
import { useConversations } from '../../store/hooks';
import { getGptIconAsset } from '../../Utils/image';
import { FeatureHighlightPopup } from '../Common/FeatureHighlightPopup';
import { SourcePreview } from '../SourcePreview/SourcePreview';
import { ConversationStarters } from './ConversationStarters';
import './ConversationView.scss';
import { PromptList } from './PromptList';
import StopCompletionButton from './StopCompletionButton';
import { UserQuery } from './UserQuery';
import { WelcomeBox } from './WelcomeBox';

const styles = makeStyles({
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...shorthands.padding('20px', '40px'),
        // Reduce header padding on mobile
        '@media (max-width: 768px)': {
            ...shorthands.padding('10px', '20px'),
        },
    },
    gptTagHeader: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...shorthands.gap('10px'),
        ...shorthands.padding('5px'),
        backgroundColor: tokens.colorNeutralBackground3,
        ...shorthands.borderRadius(tokens.borderRadiusLarge),
    },
});

// represents the conversation view, which contains the list of messages, the user query input, the welcome box and the source preview panel (if enabled by the user, e.g. for RAG tools)
export interface ConversationProps {
    canSubmit: boolean;
    data: IConversation;
    isUploadAttachmentOver: boolean;
    onUpdate: (actionType: ActionTypes, updatedPromptIndex: number, data: IConversation) => void;
    onSendQuery: (query: string, plugins: string[], modelName: string) => boolean | Promise<boolean>;
    onDocumentUpload: (files: FileList, source: AttachmentSourceType) => void;
    onDocumentRemove: (filename: string) => void;
    onUpdateAnsweredQuery: (updatedPromptIndex: number, newQuery: string) => void;
    completionInProgress: boolean;
    onStopCompletion: () => void;
    restoreLastQueryTrigger?: number;
    restoreReason?: 'error' | 'stop';
}

export const ConversationView: FC<ConversationProps> = (props: ConversationProps) => {
    const classes = styles();
    const { canSubmit, completionInProgress, data, isUploadAttachmentOver, onUpdate, onSendQuery, onDocumentUpload, onDocumentRemove, onStopCompletion, onUpdateAnsweredQuery, restoreLastQueryTrigger, restoreReason } = props;
    const { activeGpt } = useConversations();
    const [sourcePreview, setSourcePreview] = useState<ISource | undefined>(undefined);
    const [starterToggle, setStarterToggle] = useState<boolean>(false);
    const starterRef = useRef<string>('');
    const { t } = useTranslation();
    const [userInput, setUserInput] = useState<string>(''); // Add state for user input
    const [clearEditorTrigger, setClearEditorTrigger] = useState<number>(0); // Trigger to clear editor
    const initialConversationId = useRef<string | undefined>(undefined);

    const handleSelectConversationStarter = (starter: string) => {
        starterRef.current = starter;
        // Toggle boolean to force update even if same starter is selected
        setStarterToggle((prev) => !prev);
    };    const getStarter = useCallback(() => {
        return starterRef.current;
    }, [starterToggle]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (initialConversationId.current === undefined || initialConversationId.current === "DRAFT") {
            initialConversationId.current = data.id;
        }
        starterRef.current = '';
        setUserInput('');
        if (data.id !== initialConversationId.current) {
            setClearEditorTrigger(prev => prev + 1);
        }
    }, [data.id]);

    const isMobile = useIsMobile();

    return (
        <div className={`conversationView ${isMobile ? 'mobile' : ''}`}>
            <div>
                <div className={classes.header}>
                    <div className={classes.gptTagHeader} style={{ visibility: 'hidden' }} >
                        <Image src={getGptIconAsset(activeGpt?.icon)} height={30} />
                        {
                            // since SyGPT already has a text within its logo, do not display the name when it is a SyGPT conversation
                            activeGpt?.isDefault ? <></> : <Text weight='semibold'>{activeGpt?.name}</Text>
                        }
                    </div>
                    <div className={'conversationHeaderButtons'}>
                        {/* <Tooltip content={'Suggest an idea'} relationship='label'>
                            <Button
                                shape='circular'
                                appearance='primary'
                                icon={<LightbulbFilled />}
                                onClick={() => window.open('https://docs.google.com/forms/d/e/1FAIpQLSez-Y66Wac_o13XxBEPTAqOw1dYMHDMlkJDQLfi0Ko-4YjbcA/viewform?usp=dialog', '_blank')}
                            ></Button>
                        </Tooltip> */}
                        {/* <Tooltip content={'Contact us'} relationship='label'>
                            <a href='mailto:syensqo.ai@syensqo.com'>
                                <Button shape='circular' appearance='primary' icon={<MailFilled />}></Button>
                            </a>
                        </Tooltip> */}
                        <Tooltip content={'Open Wiki for help'} relationship='label'>
                            <Button
                                shape='circular'
                                appearance='primary'
                                icon={<QuestionCircleFilled />}
                                onClick={() => window.open('https://wiki.syensqo.com/display/IC/Internal+ChatGPT+Home', '_blank')}
                            ></Button>
                        </Tooltip>
                    </div>
                </div>
                <Divider />
            </div>
            {
                // shows the list of messages if there are any for the specific conversation, otherwise shows the welcome box
                data.prompts && data.prompts.length > 0 ? (
                    <>
                        <PromptList
                            key={`promptList_${data.id}`}
                            prompts={data.prompts ?? []}
                            onUpdate={(actionType, prompts, updatedPromptIndex) => {
                                onUpdate(actionType, updatedPromptIndex, { ...data, prompts });
                            }}
                            onUpdateAnsweredQuery={onUpdateAnsweredQuery}
                            onSourceClick={(source) => setSourcePreview(source)}
                        />
                    </>
                ) : (
                    <>
                        <div className="welcome-section">
                            <FeatureHighlightPopup
                                id={'mainHighLights'}
                                header={t('featureHighlights.header')}
                                title={t('featureHighlights.mainHighLights.title')}
                                content={t('featureHighlights.mainHighLights.content')}
                                link={t('featureHighlights.mainHighLights.link')}
                            >
                                <WelcomeBox key={`welcomeBox${data.id}`} />
                            </FeatureHighlightPopup>
                        </div>
                        <ConversationStarters setSelectedConversationStarter={handleSelectConversationStarter} />
                    </>
                )
            }
            {/* If there is no prompt, show the suggested prompts */}
            <StopCompletionButton completionInProgress={completionInProgress} onStopCompletion={onStopCompletion} />
            <UserQuery
                isUploadAttachmentOver={isUploadAttachmentOver}
                conversationStarterQuery={getStarter}
                canSubmit={canSubmit}
                completionInProgress={completionInProgress}
                onSendQuery={onSendQuery}
                onUploadDocumentAdd={onDocumentUpload}
                onUploadDocumentRemove={onDocumentRemove}
                hideModelSelection={(data.prompts && data.prompts.length> 0)  || ( data.gpt && data.gpt!.type == GptTypes.SyensqoInformationGpt ) ? true : false}
                restoreLastQueryTrigger={restoreLastQueryTrigger}
                inputValue={userInput} // Pass input value
                onInputChange={setUserInput} // Pass setter
                clearEditorTrigger={clearEditorTrigger} // Pass clear trigger
                restoreReason={restoreReason}
            />
            <OverlayDrawer open={!!sourcePreview} position='end' size='medium'>
                <DrawerHeaderTitle action={<Button appearance='subtle' aria-label='Close' icon={<Dismiss24Regular />} 
                    onClick={() => setSourcePreview(undefined)} />} />
                <DrawerBody>{sourcePreview && <SourcePreview source={sourcePreview} conversationId={data.id}></SourcePreview>}</DrawerBody>
            </OverlayDrawer>
        </div>
    );
};
